import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiClientError } from '../api/client.js';
import type { LeagueMemberRecord, SeasonRecord } from '../api/types.js';
import { useAppState } from './AppState.js';

export function useRequiredLeagueId(): string {
  const params = useParams();
  const leagueId = params.leagueId;
  if (!leagueId) {
    throw new Error('League route is missing leagueId');
  }
  return leagueId;
}

export function useRequiredMatchDayId(): string {
  const params = useParams();
  const matchDayId = params.matchDayId;
  if (!matchDayId) {
    throw new Error('Route is missing matchDayId');
  }
  return matchDayId;
}

export function useRequiredMatchId(): string {
  const params = useParams();
  const matchId = params.matchId;
  if (!matchId) {
    throw new Error('Route is missing matchId');
  }
  return matchId;
}

export function useRequiredSeasonId(): string {
  const params = useParams();
  const seasonId = params.seasonId;
  if (!seasonId) {
    throw new Error('Route is missing seasonId');
  }
  return seasonId;
}

export function useRequiredPlayerId(): string {
  const params = useParams();
  const playerId = params.playerId;
  if (!playerId) {
    throw new Error('Route is missing playerId');
  }
  return playerId;
}

export function useLeagueMembers(leagueId: string, enabled: boolean = true) {
  const { client, player } = useAppState();
  return useQuery({
    queryKey: ['members', leagueId],
    enabled: Boolean(client && player && leagueId && enabled),
    queryFn: async () => (await client!.getMembers(leagueId)).members,
  });
}

export function useLeagueSeasons(leagueId: string, enabled: boolean = true) {
  const { client, player } = useAppState();
  return useQuery({
    queryKey: ['seasons', leagueId],
    enabled: Boolean(client && player && leagueId && enabled),
    queryFn: async () => (await client!.getSeasons(leagueId)).seasons,
  });
}

export function useActiveSeasonRecord(leagueId: string, enabled: boolean = true) {
  const { client, player } = useAppState();
  return useQuery<SeasonRecord | null>({
    queryKey: ['active-season', leagueId],
    enabled: Boolean(client && player && leagueId && enabled),
    queryFn: async () => {
      try {
        const result = await client!.getActiveSeason(leagueId);
        return result.season;
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useResolvedActiveSeason(leagueId: string) {
  const { selectedSeasonIds, setSelectedSeasonId, resetSeasonSelection } = useAppState();
  const seasonsQuery = useLeagueSeasons(leagueId);
  const activeSeasonQuery = useActiveSeasonRecord(leagueId, seasonsQuery.status === 'success');
  const selectedSeasonId = selectedSeasonIds.get(leagueId) ?? null;

  useEffect(() => {
    if (!seasonsQuery.data) {
      return;
    }
    if (selectedSeasonId && seasonsQuery.data.some((season) => season.id === selectedSeasonId)) {
      return;
    }
    if (selectedSeasonId && seasonsQuery.data.every((season) => season.id !== selectedSeasonId)) {
      resetSeasonSelection(leagueId);
      return;
    }
    if (activeSeasonQuery.data) {
      setSelectedSeasonId(leagueId, activeSeasonQuery.data.id);
      return;
    }
    if (activeSeasonQuery.data === null) {
      setSelectedSeasonId(leagueId, null);
    }
  }, [
    activeSeasonQuery.data,
    leagueId,
    resetSeasonSelection,
    seasonsQuery.data,
    selectedSeasonId,
    setSelectedSeasonId,
  ]);

  const season = useMemo(() => {
    if (!seasonsQuery.data) {
      return null;
    }
    if (!selectedSeasonId) {
      return null;
    }
    return seasonsQuery.data.find((candidate) => candidate.id === selectedSeasonId) ?? null;
  }, [seasonsQuery.data, selectedSeasonId]);

  return {
    seasonsQuery,
    activeSeasonQuery,
    season,
    selectedSeasonId,
  };
}

export function useLeagueRole(leagueId: string): { role: LeagueMemberRecord['role'] | null; isLoading: boolean } {
  const { player } = useAppState();
  const membersQuery = useLeagueMembers(leagueId);

  const role = useMemo(() => {
    if (!player || !membersQuery.data) {
      return null;
    }
    const membership = membersQuery.data.find((member) => member.player_id === player.id && !member.is_deleted);
    return membership?.role ?? null;
  }, [membersQuery.data, player]);

  return {
    role,
    isLoading: membersQuery.isLoading,
  };
}

export function useSyncLeagueFromRoute(leagueId: string) {
  const { activeLeagueId, leagues, setActiveLeague } = useAppState();
  const navigate = useNavigate();

  useEffect(() => {
    if (!leagueId) {
      return;
    }
    if (activeLeagueId !== leagueId) {
      setActiveLeague(leagueId);
    }
    if (leagues.length > 0 && leagues.every((league) => league.id !== leagueId)) {
      navigate('/leagues', { replace: true });
    }
  }, [activeLeagueId, leagueId, leagues, navigate, setActiveLeague]);
}

export function useInvalidateQueries() {
  const queryClient = useQueryClient();
  return {
    invalidate: async (...keys: QueryKey[]) => {
      await Promise.all(keys.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    },
    queryClient,
  };
}
